import { Action } from 'redux';

import { NodeId } from 'modules/workflows/types';
import { isNewWorkflow } from 'modules/workflows/utils';
import {
  AddNodeAction,
  AddNodeErrorsAction,
  AddNodesAction,
  CloneNodesAction,
  CreateWorkflowDraftResponseAction,
  DeleteNodeAction,
  DeleteNodesAction,
  DeselectNodeAction,
  DeselectNodesAction,
  FetchMetricsResponseAction,
  FetchVisitorCountsResponseAction,
  FetchWorkflowResponseAction,
  ImportTilesetAction,
  OpenNodeRequestAction,
  OpenNodeResponseAction,
  ReassignNodeIdAction,
  SaveWorkflowDraftResponseAction,
  SelectNodesAction,
  SelectGenAINodesAction,
  UpdateNodeAction,
  UpdateNodeDataAction,
  UpdateNodeComboIdAction,
  UpdateNodesAction,
  UpdateNotesVisibilityAction,
  actionTypes as t,
  JourneyVisitorMetricsResponseAction,
} from '../actions';
import { WorkflowState } from '../ducks.types';
import {
  // next node id
  INITIAL_NODE_ID,
  addNode,
  addNodeErrors,
  addNodes,
  cloneNodes,
  deleteNode,
  deleteNodes,
  deselectNode,
  deselectNodes,
  incrementNextNodeId,
  incrementNextNodeIdBy,
  removeNodeError,
  selectNodes,
  selectGenAINodes,
  setInitialNodes,
  setNextNodeId,
  setOriginalNodes,
  setSendMetrics,
  setVisitorCounts,
  updateNode,
  updateNodeComboId,
  updateNodes,
  updateStickyNotesVisibility,
  setVisitorMetrics,
  updateNodesOnWorkflowViewChange,
} from './utils';
import { WorkflowViewAction } from '../actions/action.types';

/** Actions called directly after fetching a workflow from the backend */
type FetchWorkflowResponseActions =
  | CreateWorkflowDraftResponseAction
  | FetchWorkflowResponseAction
  | SaveWorkflowDraftResponseAction;

export const nodesReducer = (
  state: WorkflowState['nodes'] = {},
  action: Action,
  wfState: WorkflowState,
) => {
  switch (action.type) {
    case t.CREATE_WORKFLOW_DRAFT_RESPONSE:
    case t.SAVE_WORKFLOW_DRAFT_RESPONSE:
    case t.FETCH_WORKFLOW_RESPONSE: {
      return setInitialNodes(state, action as FetchWorkflowResponseActions, wfState);
    }
    case t.ADD_NODE: {
      return addNode(state, action as AddNodeAction, wfState);
    }
    case t.ADD_NODES: {
      return addNodes(state, action as AddNodesAction, wfState);
    }
    case t.CLONE_NODES: {
      return cloneNodes(state, action as CloneNodesAction, wfState);
    }
    case t.DELETE_NODE: {
      return deleteNode(state, action as DeleteNodeAction);
    }
    case t.DELETE_NODES: {
      return deleteNodes(state, action as DeleteNodesAction);
    }
    case t.SELECT_NODES: {
      return selectNodes(state, action as SelectNodesAction);
    }
    case t.SELECT_GEN_AI_NODES: {
      return selectGenAINodes(state, action as SelectGenAINodesAction);
    }
    case t.DESELECT_NODES: {
      return deselectNodes(state, action as DeselectNodesAction);
    }
    case t.CLOSE_NODE:
    case t.DESELECT_NODE: {
      return deselectNode(state, action as DeselectNodeAction, wfState);
    }
    case t.UPDATE_NODE: {
      return updateNode(state, action as UpdateNodeAction, wfState);
    }
    case t.ADD_NODE_ERRORS: {
      return addNodeErrors(state, action as AddNodeErrorsAction);
    }
    case t.REMOVE_NODE_ERROR: {
      return removeNodeError(state, action, wfState);
    }
    case t.UPDATE_NODE_COMBO_ID: {
      return updateNodeComboId(state, action as UpdateNodeComboIdAction);
    }
    case t.UPDATE_NODES: {
      return updateNodes(state, action as UpdateNodesAction, wfState);
    }
    case t.UPDATE_NODE_DATA: {
      // TODO: decouple deselection from node data update
      const { payload } = action as UpdateNodeDataAction;
      const { nodeData, options } = payload;
      const nodeId = (payload.nodeId || wfState.openedNodeId) as NodeId;
      const shouldDeselect = options?.closeEditor !== false;
      // update the node data
      const updatedState = updateNode(
        state,
        { ...action, payload: { ...payload, node: { id: nodeId, nodeData } } },
        wfState,
      );
      return shouldDeselect
        ? // deselect the node by default
          deselectNode(updatedState, { ...action, payload: { nodeId } }, wfState)
        : // otherwise return the updated state without deselecting the node
          updatedState;
    }
    case t.OPEN_NODE_RESPONSE: {
      const { payload } = action as OpenNodeResponseAction;
      const { nodeId, resources, nodeUpdates } = payload;
      const prevNode = state[nodeId] || {};
      const node = {
        ...prevNode,
        ...nodeUpdates,
        resources: {
          ...prevNode?.resources,
          ...resources,
        },
      };
      return updateNode(
        state,
        { ...action, payload: { ...payload, node, options: { shouldValidate: false } } },
        wfState,
      );
    }
    case t.FETCH_SEND_METRICS_RESPONSE: {
      return setSendMetrics(state, action as FetchMetricsResponseAction, wfState);
    }
    case t.FETCH_VISITOR_COUNTS_RESPONSE: {
      return setVisitorCounts(state, action as FetchVisitorCountsResponseAction, wfState);
    }
    case t.FETCH_JOURNEY_VISITOR_METRICS_RESPONSE: {
      return setVisitorMetrics(state, action as JourneyVisitorMetricsResponseAction, wfState);
    }
    case t.IMPORT_TILESET: {
      return { ...state, ...(action as ImportTilesetAction).payload.nodes };
    }
    /**
     * Notes specific changes
     */
    case t.SHOW_NOTES: {
      return updateStickyNotesVisibility(true, state, action, wfState);
    }
    case t.HIDE_NOTES: {
      return updateStickyNotesVisibility(false, state, action, wfState);
    }
    case t.TOGGLE_NOTES_VISIBILITY: {
      return updateStickyNotesVisibility(!wfState.isNotesVisible, state, action, wfState);
    }
    case t.UPDATE_NOTES_VISIBILITY: {
      const { payload } = action as UpdateNotesVisibilityAction;
      return updateStickyNotesVisibility(payload.isVisible, state, action, wfState);
    }
    case t.REASSIGN_NODE_ID: {
      const { prevNodeId, nodeId } = (action as ReassignNodeIdAction).payload;
      const oldNode = state[prevNodeId];
      if (oldNode) {
        const nextState = { ...state, [nodeId]: { ...oldNode, id: `${nodeId}` } };
        return deleteNode(nextState, { ...action, payload: { nodeId: prevNodeId } });
      }
      return state;
    }
    case t.UPDATE_WORKFLOW_VIEW: {
      return updateNodesOnWorkflowViewChange(state, action as WorkflowViewAction, wfState);
    }
    default:
      return state;
  }
};

export const openedNodeIdReducer = (state: NodeId | null = null, action: Action) => {
  switch (action.type) {
    case t.UPDATE_NODE_DATA:
      return (action as UpdateNodeDataAction).payload?.options?.closeEditor === false
        ? state
        : null;
    case t.CLOSE_NODE:
    case t.FETCH_WORKFLOW_RESPONSE:
      return null;
    case t.OPEN_NODE_REQUEST:
      return (action as OpenNodeRequestAction).payload.nodeId;
    default:
      return state;
  }
};

export const nextNodeIdReducer = (state = INITIAL_NODE_ID, action: Action) => {
  switch (action.type) {
    case t.SAVE_WORKFLOW_DRAFT_REQUEST: // TODO: remove and only put in the response
    case t.FETCH_WORKFLOW_RESPONSE: {
      const { payload } = action as SaveWorkflowDraftResponseAction | FetchWorkflowResponseAction;
      return setNextNodeId(state, {
        payload: { id: isNewWorkflow(payload.workflow?.id) ? -2 : -1 },
      });
    }
    case t.IMPORT_TILESET: {
      const { payload } = action as ImportTilesetAction;
      return setNextNodeId(state, {
        payload: { id: payload.nextNodeId },
      });
    }
    case t.ADD_NODE: {
      return incrementNextNodeId(state);
    }
    case t.CLONE_NODES:
    case t.ADD_NODES: {
      const { nodes } = (action as CloneNodesAction | AddNodesAction).payload;
      const num = (nodes || []).length;
      return incrementNextNodeIdBy(state, { payload: { num } });
    }
    default:
      return state;
  }
};

export const isNodeOpeningReducer = (state = false, action: Action) => {
  switch (action.type) {
    case t.OPEN_NODE_REQUEST:
      return true;
    case t.OPEN_NODE_RESPONSE:
    case t.OPEN_NODE_ERROR:
      return false;
    default:
      return state;
  }
};

export const originalNodesReducer = (
  state: WorkflowState['originalNodes'] = [],
  action: Action,
) => {
  switch (action.type) {
    case t.CREATE_WORKFLOW_DRAFT_RESPONSE:
    case t.SAVE_WORKFLOW_DRAFT_RESPONSE:
    case t.FETCH_WORKFLOW_RESPONSE: {
      return setOriginalNodes(state, action as FetchWorkflowResponseActions);
    }
    default:
      return state;
  }
};
